/**
 * @file summary-info-card.tsx
 * @module app/modules/_common/components/summary-info-card
 * @description Reusable summary information card component for UTrade Admin.
 * Renders a responsive metric tile with value and label slots, optional danger
 * emphasis, and configurable Bootstrap column sizing for dashboard-style layouts.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.2.0
 * @version 2.1.2
 */

import { forwardRef, ReactNode } from 'react';
import { Col } from 'react-bootstrap';
import DefaultSummaryIcon from '../../../../_admin/assets/media/dashboard/master.svg';
import '../styles/summary-info-card.css';

interface SummaryInfoCardProps {
  bgClassName?: string;
  value: ReactNode;
  label: ReactNode;
  icon?: ReactNode;
  danger?: boolean;
  valueClassName?: string;
  colKey?: string | number;
  onClick?: () => void;
  colProps?: {
    xl?: number;
    md?: number;
    xs?: number;
  };
}

// forwardRef + rest-prop spread so OverlayTrigger can attach its ref and hover/focus handlers
const SummaryInfoCard = forwardRef<
  HTMLDivElement,
  SummaryInfoCardProps & Record<string, any>
>(
  (
    {
      bgClassName = '',
      value,
      label,
      icon = <img src={DefaultSummaryIcon} alt="summary icon" width={17} height={17} />,
      danger = false,
      valueClassName,
      colKey,
      onClick,
      colProps,
      ...rest
    },
    ref
  ) => {
    return (
      <Col key={colKey} xl={colProps?.xl ?? 3} md={colProps?.md ?? 6} xs={colProps?.xs ?? 6}>
        <div
          ref={ref}
          className={`uw-summary-card border-r8px rounded py-3 px-4 h-100${onClick ? ' cursor-pointer' : ''
            } ${bgClassName}`}
          onClick={onClick}
          role={onClick ? 'button' : undefined}
          {...rest}
        >
          <div className="uw-summary-card__header d-flex align-items-center justify-content-between gap-2">
            <div className="uw-summary-card__label fw-600">{label}</div>
            <span className="uw-summary-card__icon">{icon}</span>
          </div>
          <div
            className={`uw-summary-card__value ${valueClassName || 'fs-22 fw-bolder'
              }${danger ? ' text-danger' : ''}`}
          >
            {value}
          </div>
        </div>
      </Col>
    );
  }
);

export default SummaryInfoCard;